import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { environment } from '../../../environments/environment';
import { faPowerOff, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import {
	AuthService,
	IAppDashboard,
	IAuthProvider,
	IDropDownMenuItem,
	IMenuItem,
	IMenuItemPermissions,
	IPluginPermissions,
	IUser,
	IUserRole,
	ModelService,
	PluginInjectorService
} from 'core-client/api';
import { SharedModule } from '../../shared/shared.module';
import { FaLayersComponent } from "@fortawesome/angular-fontawesome";
import { UserAvatarComponent } from 'core-plugin';

@Component({
  selector: "app-header",
  templateUrl: "./header.component.html",
  imports: [SharedModule, UserAvatarComponent, FaLayersComponent],
  styleUrls: ["./header.component.scss"],
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Input() isMobileView: boolean = false;
  @Input() loggedUser!: IUser;
  private readonly ngUnsubscribe$: Subject<void> = new Subject<void>();
  appName: string;
  secMenuVisible: boolean = false;
  private permissions: IPluginPermissions[] = [];
  currentDashboard: IAppDashboard | undefined;

  constructor(
    private readonly authService: AuthService,
    private readonly dialog: MatDialog,
    public pluginInjectorService: PluginInjectorService,
    private readonly modelService: ModelService,
    private readonly translate: TranslateService,
  ) {
    this.appName = environment.appTitle ? environment.appTitle : "ADDboard";
  }

  ngOnInit(): void {
    this.modelService._user
      .observeAllUsers$()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(async (users: IUser[]) => {
        for (const user of users) {
          if (user._id === this.loggedUser._id) {
            this.modelService._user.setUser(user);
            const activeDashboard = user.app_dashboards.find(
              (dashboard: IAppDashboard) =>
                dashboard._id === user.active_dashboard,
            );

            if (
              activeDashboard &&
              activeDashboard._id !== this.loggedUser.active_dashboard
            ) {
              this.modelService._appDashboard.setActiveAppDashboard(
                activeDashboard,
              );
            }
          }
        }
      });

    this.modelService._appDashboard
      .observeActiveAppDashboard$()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(async (dashboard: IAppDashboard) => {
        if (this.loggedUser._id === this.modelService._user.getUser()._id) {
          this.currentDashboard = dashboard;
          this.permissions =
            await this.pluginInjectorService.permissionService.getPermissions(
              dashboard,
              this.modelService._user.getUser(),
            );

          this.pluginInjectorService.loadPluginMenuItems(dashboard);
        }
      });

    this.modelService._userRole
      .observeRoleModification$()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(async (userRole: { _id: string }) => {
        const ownsRole = this.modelService._user
          .getUser()
          .roles.some((role: IUserRole) => role._id === userRole._id);

        if (ownsRole) {
          const activeDashboard = await this.modelService._appDashboard.getById(
            this.modelService._user.getUser().active_dashboard,
          );

          if ("_id" in activeDashboard) {
            this.modelService._appDashboard.setActiveAppDashboard(
              activeDashboard,
            );
          }
        }
      });

  }

  logout: {
    name: string;
    callback: () => Promise<void>;
    icon: IconDefinition;
  } = {
    name: "Logout",
    callback: async () => await this.authService.logout(),
    icon: faPowerOff,
  };

  toggleMenu(): void {
    this.secMenuVisible = !this.secMenuVisible;
  }

  async triggerMenuCallback(
    menu: IDropDownMenuItem | IMenuItem,
    index: number,
    parentMenu?: IMenuItem,
  ): Promise<void> {
    if (menu.callback) {
      const menuId = parentMenu?.nameIdentifier ?? menu.nameIdentifier;
      if (menu.useExternalLink) {
        const exterUserProfileUrl = await this.buildExternLinkUrl(menuId);
        await menu.callback(this.dialog, menuId, exterUserProfileUrl);
      } else {
        await menu.callback(this.dialog, menuId);
      }
    }
  }

  async buildExternLinkUrl(menuId: string): Promise<string> {
    let externalLink = "";
    if (menuId === "USER_PROFILE") {
      const user: IUser = this.modelService._user.getUser();
      const authProviderDetails = user.auth;
      if (authProviderDetails.length === 1) {
        const populateAuthSpecs = authProviderDetails[0];
        const authProvider = await this.modelService._authProvider.getById(
          populateAuthSpecs.provider._id,
        );
        if ("_id" in authProvider) {
          externalLink = this.createAuthProviderUserProfileLink(authProvider);
        }
      }
    }

    return externalLink;
  }

  createAuthProviderUserProfileLink(authProvider: IAuthProvider): string {
    const endpointUrl = authProvider.config.endpoint_url;
    return `${endpointUrl}/account/`;
  }

  getAltIcon(menu: IMenuItem): false | IconDefinition | undefined {
    return (
      (menu.altIconActive ? menu.altIconActive.value : false) && menu.altIcon
    );
  }

  // Simple header menu permission access verification
  hasMenuAccess(menu: IMenuItem): boolean {
    if (menu.accessRights.length === 0) {
      return true;
    }

    // Extract base permissions (list/view) from accessRights
    const basePermissions = menu.accessRights.filter(
      (right) => right.includes("_list") || right.includes("_view"),
    );

    // If there are no base permissions defined, fall back to checking any permission
    // This handles cases where a menu might only have create/edit/delete operations
    if (basePermissions.length === 0) {
      return this.checkIfUserHasAnyPermission(menu);
    }

    // User must have at least ONE base permission to see the menu
    let isValid = false;
    for (const pluginPermission of this.permissions) {
      if (
        pluginPermission.plugin_id === menu.pluginId &&
        pluginPermission.menuitem_permissions
      ) {
        isValid = this.hasRequiredPermissions(
          pluginPermission.menuitem_permissions,
          menu.pluginModule.toLowerCase(),
          basePermissions, // Only check base permissions!
        );

        if (isValid) break;
      }
    }

    return isValid;
  }

  // Helper method for backward compatibility
  private checkIfUserHasAnyPermission(menu: IMenuItem): boolean {
    let isValid = false;
    for (const pluginPermission of this.permissions) {
      if (
        pluginPermission.plugin_id === menu.pluginId &&
        pluginPermission.menuitem_permissions
      ) {
        isValid = this.hasRequiredPermissions(
          pluginPermission.menuitem_permissions,
          menu.pluginModule.toLowerCase(),
          menu.accessRights,
        );

        if (isValid) break;
      }
    }
    return isValid;
  }

  // DropDown menu access verification
  canAccessDropdownMenu(menu: IMenuItem, dropdown: IDropDownMenuItem): boolean {
    if (!dropdown.accessReference) {
      return true; // No access rights required, allow by default
    }

    for (const pluginPermission of this.permissions) {
      if (
        pluginPermission.plugin_id === menu.pluginId &&
        pluginPermission.menuitem_permissions
      ) {
        if (
          this.hasRequiredDropdownMenuAccess(
            pluginPermission.menuitem_permissions,
            dropdown,
          )
        ) {
          return true; // Exit early if access is found
        }
      }
    }
    return false;
  }

  // Verification if required permissions are set
  private hasRequiredPermissions(
    menuItemPermissions: IMenuItemPermissions,
    pluginMenuModule: string,
    menuAccessRights: string[],
  ): boolean {
    const modulePermissions = menuItemPermissions[pluginMenuModule];

    if (!modulePermissions) {
      return false; // If module not found, deny access
    }

    return this.pluginInjectorService.permissionService.checkMenuPermissionsRecursive(
      modulePermissions,
      menuAccessRights,
    );
  }

  // Verification if dropdown menu required permissions are set
  private hasRequiredDropdownMenuAccess(
    menuItemPermissions: IMenuItemPermissions,
    dropdown: IDropDownMenuItem,
  ): boolean {
    // Ensure pluginModule is defined
    if (!dropdown.pluginModule) {
      return false;
    }

    const moduleKey = dropdown.pluginModule.toLowerCase();

    // Only get permissions for this specific module
    const modulePermissions = menuItemPermissions[moduleKey];

    if (!modulePermissions) {
      return false; // No permissions found for this module
    }

    // Validate that accessReference permissions belong to this module
    if (typeof dropdown.accessReference === "string") {
      // Single string reference
      if (!this.permissionExistsInModule(dropdown.accessReference, dropdown)) {
        return false;
      }
    } else if (Array.isArray(dropdown.accessReference)) {
      // Array of references
      const allPermissionsValid = dropdown.accessReference.every((ref) =>
        this.permissionExistsInModule(ref, dropdown),
      );

      if (!allPermissionsValid) {
        return false;
      }
    }

    return this.pluginInjectorService.permissionService.checkDropdownMenuPermissionsRecursive(
      modulePermissions,
      dropdown,
      this.currentDashboard,
    );
  }

  // Helper method to verify permission belongs to module
  private permissionExistsInModule(
    permissionRef: string,
    dropdown: IDropDownMenuItem,
  ): boolean {
    if (!dropdown.pluginModule) {
      return false;
    }

    const permissionLower = permissionRef.toLowerCase();
    const pluginNameLower = dropdown.pluginName.toLowerCase();
    const moduleLower = dropdown.pluginModule.toLowerCase();

    // Permission should contain: {plugin_name}_{module}_
    // e.g., "core_plugin_dashboard_" or "my_custom_plugin_reports_"
    const expectedPattern = `${pluginNameLower}_${moduleLower}_`;
    return permissionLower.includes(expectedPattern);
  }

  /** Resolves the accessible label / tooltip text for a primary menu item. */
  getMenuAriaLabel(menu: IMenuItem): string {
    if (this.getAltIcon(menu) && menu.altName) {
      return this.translate.instant(
        `${menu.pluginName}.${menu.pluginModule}.${menu.altName}`,
      );
    }
    if (menu.pluginModule) {
      return this.translate.instant(
        `${menu.pluginName}.${menu.pluginModule}.${menu.nameIdentifier}`,
      );
    }
    return this.translate.instant(`${menu.pluginName}.${menu.nameIdentifier}`);
  }

  /** Aggregate badge count across all accessible menu items (used on the mobile hamburger icon). */
  get hamburgerBadgeTotal(): number {
    const menu = this.pluginInjectorService.getMenu();
    return [...menu.primary, ...menu.secondary]
      .filter((m: IMenuItem) => this.hasMenuAccess(m) && m.badge$)
      .reduce((sum: number, m: IMenuItem) => sum + ((m.badge$ as BehaviorSubject<number>).getValue()), 0);
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
